import {debug} from '@acng/frontend-bounty/dom/debug.js';
import {remove} from '@acng/frontend-bounty/dom/move.js';
import {join} from '@acng/frontend-bounty/std/array.js';
import {matchAllMap} from '@acng/frontend-bounty/std/string.js';
import {AttributeSetup} from '@acng/frontend-stargazer';
import {globalValue} from '@acng/frontend-relativity';

import {ctxAuthUser} from '../context/auth-user.js';

/**
 * ## Stargazer DOM Attribute
 *
 * | Phase   | Description                                                 |
 * | ------- | ----------------------------------------------------------- |
 * | Compile | Remove the element if the split group condition is not met. |
 *
 * ## Options
 *
 * - "none", not assigned (including guests)
 * - "test-group", assigned to a group number larger than `0`
 * - "control-group", not assigned or assigned to the group number `0`
 * - {number}, assigned to the group number {number}
 *
 * Multiple group conditions can be specified.
 *
 * @author Jacob Viertel [jv@onscreen.net](mailto:jv@onscreen.net)
 *
 * @since 3.87.0
 *
 * @example
 *
 * ```html
 * <div user-split="none@123">not assigned to test #123</div>
 * <div user-split="control-group@123">control group of test #123</div>
 * <div user-split="test-group@123">test group of test #123</div>
 * <div user-split="3@123">group #3 of test #123</div>
 * <div user-split="none@123; 3@123">
 *   group #3 of test #123 OR not assigned
 * </div>
 * ```
 *
 * @group DOM Attribute
 */
export const USER_SPLIT = 'user-split';

AttributeSetup(USER_SPLIT, (attributeName) => {
  const NONE = 'none';
  const CONTROL_GROUP = 'control-group';
  const TEST_GROUP = 'test-group';
  const syntax = new RegExp(`(\\d+|${join([NONE, CONTROL_GROUP, TEST_GROUP], '|')})@(\\d+)`, 'g');

  return (element, attributeValue) => {
    const options = matchAllMap(attributeValue, syntax, (option) => {
      let result;

      const splitTestId = Number(option[2]);
      const assignment = globalValue(ctxAuthUser)?.split_tests.find(({id}) => id == splitTestId);

      switch (option[1]) {
        case NONE:
          result = !assignment;
          break;
        case CONTROL_GROUP:
          result = !assignment || !assignment.group;
          break;
        case TEST_GROUP:
          result = assignment && assignment.group > 0;
          break;
        default:
          result = assignment?.group === Number(option[1]);
      }

      DEBUG: if (debug(element)) {
        console.debug(`[${attributeName}] check`, {element, attributeValue, option, result, assignment});
      }

      return result;
    });

    DEBUG: if (debug(element)) {
      if (options.includes(true)) {
        console.info(`[${attributeName}] confirmed`, {element, attributeValue, options, syntax});
      } else {
        console.info(`[${attributeName}] removed`, {element, attributeValue, options, syntax});
      }
    }

    if (!options.includes(true)) {
      remove(element);
    }
  };
});
